/**
 * Shared collection view — read-only bottles list for a shared user
 */

import { useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, NavLink, useNavigate } from 'react-router-dom';
import { Table, Pagination, Modal, Button } from '../components/common';
import { BottleForm } from '../components/bottles';
import { useSharedBottles, useSharedWithMe, useCopySharedDistillery } from '../hooks/useSharing';
import { useCreateBottle } from '../hooks/useBottles';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useToast } from '../context/ToastContext';
import { distilleryService } from '../services/distilleryService';
import { formatCurrency, formatProof, formatDate, formatAge } from '../utils/formatters';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';
import './SharedCollection.css';

const COLUMNS = [
  {
    key: 'bottle_name',
    label: 'Name',
    sortable: true,
    render: (value) => <span className="bottle-list__name-cell">{value}</span>
  },
  {
    key: 'distilleries',
    label: 'Distillery',
    sortable: false,
    render: (value) => value?.distillery_name || '-'
  },
  { key: 'type', label: 'Type', sortable: true },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    render: (value) => (
      <span className={`table__status table__status--${value.toLowerCase()}`}>
        {value}
      </span>
    )
  },
  {
    key: 'proof',
    label: 'Proof',
    sortable: true,
    render: (value) => formatProof(value)
  },
  {
    key: 'purchase_date',
    label: 'Purchased',
    sortable: true,
    render: (value) => formatDate(value)
  },
  {
    key: 'purchase_price',
    label: 'Price',
    sortable: true,
    render: (value) => formatCurrency(value)
  }
];

function SharedBottleDetail({ bottle, onCopyBottle, onCopyDistillery, copyingDistillery }) {
  const characteristics = [
    bottle.single_barrel && 'Single Barrel',
    bottle.bottled_in_bond && 'Bottled-in-Bond',
    bottle.cask_strength && 'Cask Strength',
    bottle.small_batch && 'Small Batch',
    bottle.double_barreled && 'Double Barreled'
  ].filter(Boolean);

  return (
    <div className="shared-bottle-detail">
      <div className="shared-bottle-detail__title-row">
        <span className={`shared-bottle-detail__status shared-bottle-detail__status--${bottle.status.toLowerCase()}`}>
          {bottle.status}
        </span>
      </div>

      <div className="shared-bottle-detail__type-row">
        <span className="shared-bottle-detail__type">{bottle.type}</span>
        {bottle.size && <span className="shared-bottle-detail__size">{bottle.size}</span>}
      </div>

      {bottle.distilleries && (
        <div className="shared-bottle-detail__distillery">
          <span>{bottle.distilleries.distillery_name}</span>
          <span className="shared-bottle-detail__country">{bottle.distilleries.country}</span>
        </div>
      )}

      <div className="shared-bottle-detail__grid">
        <div className="shared-bottle-detail__field">
          <label>Proof</label>
          <p>{formatProof(bottle.proof)}</p>
        </div>
        <div className="shared-bottle-detail__field">
          <label>Age</label>
          <p>{formatAge(bottle.age_months)}</p>
        </div>
        <div className="shared-bottle-detail__field">
          <label>Purchase Price</label>
          <p>{formatCurrency(bottle.purchase_price)}</p>
        </div>
        <div className="shared-bottle-detail__field">
          <label>Purchase Date</label>
          <p>{formatDate(bottle.purchase_date)}</p>
        </div>
        {bottle.status === 'Empty' && bottle.date_emptied && (
          <div className="shared-bottle-detail__field">
            <label>Date Emptied</label>
            <p>{formatDate(bottle.date_emptied)}</p>
          </div>
        )}
        {bottle.store_pick && (
          <div className="shared-bottle-detail__field">
            <label>Store Pick</label>
            <p>{bottle.store_pick}</p>
          </div>
        )}
        {bottle.finish_cask && (
          <div className="shared-bottle-detail__field">
            <label>Finish Cask</label>
            <p>{bottle.finish_cask}</p>
          </div>
        )}
      </div>

      {characteristics.length > 0 && (
        <div className="shared-bottle-detail__characteristics">
          <label>Characteristics</label>
          <div className="shared-bottle-detail__tags">
            {characteristics.map(c => (
              <span key={c} className="shared-bottle-detail__tag">{c}</span>
            ))}
          </div>
        </div>
      )}

      {bottle.notes && (
        <div className="shared-bottle-detail__field shared-bottle-detail__field--full">
          <label>Notes</label>
          <p>{bottle.notes}</p>
        </div>
      )}

      {bottle.tasting_notes && (
        <div className="shared-bottle-detail__field shared-bottle-detail__field--full">
          <label>Tasting Notes</label>
          <p>{bottle.tasting_notes}</p>
        </div>
      )}

      {(bottle.image_front_url || bottle.image_back_url) && (
        <div className="shared-bottle-detail__images">
          <label>Images</label>
          <div className="shared-bottle-detail__images-grid">
            {bottle.image_front_url && (
              <div className="shared-bottle-detail__image-container">
                <span className="shared-bottle-detail__image-label">Front Label</span>
                <img
                  src={bottle.image_front_url}
                  alt={`${bottle.bottle_name} front label`}
                  className="shared-bottle-detail__image"
                />
              </div>
            )}
            {bottle.image_back_url && (
              <div className="shared-bottle-detail__image-container">
                <span className="shared-bottle-detail__image-label">Back Label</span>
                <img
                  src={bottle.image_back_url}
                  alt={`${bottle.bottle_name} back label`}
                  className="shared-bottle-detail__image"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="shared-bottle-detail__actions">
        {bottle.distilleries && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onCopyDistillery(bottle.distillery_id, bottle.distilleries.distillery_name)}
            loading={copyingDistillery}
          >
            Copy Distillery
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={() => onCopyBottle(bottle)}>
          Copy Bottle
        </Button>
      </div>
    </div>
  );
}

function SharedBottleMobileCard({ bottle, onClick }) {
  return (
    <div className="shared-bottle-card" onClick={() => onClick(bottle)}>
      <div className="shared-bottle-card__header">
        <h3 className="shared-bottle-card__name">{bottle.bottle_name}</h3>
        <span className={`shared-bottle-card__status shared-bottle-card__status--${bottle.status.toLowerCase()}`}>
          {bottle.status}
        </span>
      </div>
      <div className="shared-bottle-card__type">{bottle.type}</div>
      {bottle.distilleries && (
        <div className="shared-bottle-card__distillery">{bottle.distilleries.distillery_name}</div>
      )}
      <div className="shared-bottle-card__meta">
        {bottle.proof != null && <span>{formatProof(bottle.proof)}</span>}
        {bottle.purchase_price != null && <span>{formatCurrency(bottle.purchase_price)}</span>}
        {bottle.purchase_date && <span>{formatDate(bottle.purchase_date)}</span>}
      </div>
    </div>
  );
}

export default function SharedCollection() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBottle, setSelectedBottle] = useState(null);
  const [copyBottleData, setCopyBottleData] = useState(null);
  const [isResolvingBottle, setIsResolvingBottle] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const createBottle = useCreateBottle();
  const copySharedDistillery = useCopySharedDistillery();

  const handleCopyDistillery = async (distilleryId, distilleryName) => {
    try {
      const result = await copySharedDistillery.mutateAsync(distilleryId);
      if (result.created) {
        addToast({ type: 'success', message: `"${distilleryName}" added to your distilleries` });
      } else {
        addToast({ type: 'info', message: `"${distilleryName}" is already in your collection` });
      }
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to copy distillery' });
    }
  };

  const handleCopyBottle = async (bottle) => {
    setIsResolvingBottle(true);
    let distilleryId = '';
    try {
      if (bottle.distilleries?.distillery_name) {
        const match = await distilleryService.findByName(bottle.distilleries.distillery_name);
        if (match) distilleryId = match.id;
      }
    } catch (e) { /* ignore lookup errors */ }
    setCopyBottleData({ ...bottle, distillery_id: distilleryId });
    setIsResolvingBottle(false);
  };

  const handleCopyBottleSubmit = async (formData) => {
    try {
      const newBottle = await createBottle.mutateAsync(formData);
      addToast({ type: 'success', message: 'Bottle copied to your collection' });
      setCopyBottleData(null);
      setSelectedBottle(null);
      navigate(`/bottles/${newBottle.id}`);
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to copy bottle' });
    }
  };

  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;
  const sortBy = searchParams.get('sort') || 'purchase_date';
  const sortOrder = searchParams.get('order') || 'desc';
  const filters = useMemo(() => {
    const f = {};
    const search = searchParams.get('search');
    if (search) f.search = search;
    return f;
  }, [searchParams]);

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { data: sharedList = [] } = useSharedWithMe();
  const share = sharedList.find(s => s.share_id === shareId);
  const userId = share?.owner_id;
  const ownerEmail = share?.owner_email || 'Shared User';
  const ownerLabel = share?.owner_display_name || ownerEmail;
  const canViewBottles = share?.share_bottles !== false;
  const canViewHunt = share?.share_hunt !== false;

  const { data, isLoading } = useSharedBottles(userId, {
    page,
    limit: pageSize,
    sortBy,
    sortOrder,
    filters
  });

  const bottles = data?.data || [];
  const totalCount = data?.count || 0;

  return (
    <div className="shared-page">
      <div className="shared-page__header">
        <p className="shared-page__viewing-label">
          Viewing <strong title={share?.owner_display_name ? ownerEmail : undefined}>{ownerLabel}</strong>&rsquo;s collection
        </p>
        <div className="shared-page__nav-tabs">
          {canViewBottles && (
            <NavLink
              to={`/shared/${shareId}/dashboard`}
              className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
            >
              Dashboard
            </NavLink>
          )}
          <NavLink
            to={`/shared/${shareId}/bottles`}
            className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
          >
            Bottles
          </NavLink>
          {canViewHunt && (
            <NavLink
              to={`/shared/${shareId}/hunt`}
              className={({ isActive }) => `shared-page__nav-tab ${isActive ? 'shared-page__nav-tab--active' : ''}`}
            >
              Hunt List
            </NavLink>
          )}
        </div>
      </div>

      <div className="shared-page__subheader">
        <h1 className="shared-page__title">Bottle Collection</h1>
        {canViewBottles && <span className="shared-page__count">{totalCount} bottle{totalCount !== 1 ? 's' : ''}</span>}
      </div>

      {!canViewBottles ? (
        <p className="shared-page__locked">The owner has not shared their bottle collection with you.</p>
      ) : (
      <>
      <div className="shared-page__search">
        <input
          type="search"
          placeholder="Search bottles..."
          value={searchParams.get('search') || ''}
          onChange={(e) => updateParams({ search: e.target.value || null, page: 1 })}
          className="shared-page__search-input"
        />
      </div>

      {isMobile ? (
        <div className="shared-bottle-card-list">
          {isLoading ? (
            <div className="shared-bottle-card-list__loading">Loading...</div>
          ) : bottles.length === 0 ? (
            <div className="shared-bottle-card-list__empty">No bottles in this collection</div>
          ) : (
            bottles.map(bottle => (
              <SharedBottleMobileCard key={bottle.id} bottle={bottle} onClick={setSelectedBottle} />
            ))
          )}
        </div>
      ) : (
        <Table
          columns={COLUMNS}
          data={bottles}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(col, dir) => updateParams({ sort: col, order: dir, page: 1 })}
          onRowClick={setSelectedBottle}
          loading={isLoading}
          emptyMessage="No bottles in this collection"
        />
      )}

      {totalCount > 0 && (
        <Pagination
          currentPage={page}
          totalItems={totalCount}
          pageSize={pageSize}
          onPageChange={(p) => updateParams({ page: p })}
          onPageSizeChange={(size) => updateParams({ pageSize: size, page: 1 })}
        />
      )}
      </>
      )}

      <Modal
        isOpen={!!selectedBottle}
        onClose={() => setSelectedBottle(null)}
        title={selectedBottle?.bottle_name || ''}
        size="lg"
      >
        {selectedBottle && (
          <SharedBottleDetail
            bottle={selectedBottle}
            onCopyBottle={handleCopyBottle}
            onCopyDistillery={handleCopyDistillery}
            copyingDistillery={copySharedDistillery.isPending}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!copyBottleData || isResolvingBottle}
        onClose={() => setCopyBottleData(null)}
        title={`Copy "${copyBottleData?.bottle_name || ''}"`}
        size="lg"
      >
        {copyBottleData && (
          <BottleForm
            bottle={copyBottleData}
            isClone
            onSubmit={handleCopyBottleSubmit}
            onCancel={() => setCopyBottleData(null)}
            loading={createBottle.isPending}
          />
        )}
      </Modal>
    </div>
  );
}
